import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ContractsPage from './pages/contracts/ContractsPage';
import StaffPage from './pages/staff/StaffPage';
import ProfilePage from './pages/profile/ProfilePage';
import GasReadingsPage from './pages/gas-readings/GasReadingsPage';
import UsersPage from './pages/users/UsersPage';
import QuotationsPage from './pages/quotations/QuotationsPage';
import ServicesPage from './pages/services/ServicesPage';
import ServiceDetailPage from './pages/services/ServiceDetailPage';
import BookingsPage from './pages/bookings/BookingsPage';
import VisitorsPage from './pages/visitors/VisitorsPage';
import ApartmentMembersPage from './pages/members/ApartmentMembersPage';
import CalendarPage from './pages/calendar/CalendarPage';
import PaymentTrackerPage from './pages/payments/PaymentTrackerPage';
import NotificationsPage from './pages/notifications/NotificationsPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'gas_reader') {
    return <Navigate to="/gas-readings" replace />;
  }

  if (user.role === 'caretaker' && !user.allowed_features?.length) {
    return <Navigate to="/gas-readings" replace />;
  }

  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'superadmin') {
    return user.role === 'gas_reader'
      ? <Navigate to="/gas-readings" replace />
      : <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'gas_reader') {
      return <Navigate to="/gas-readings" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (user?.role === 'gas_reader') {
    return <Navigate to="/gas-readings" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
      <Route path="/contracts" element={<AdminRoute><ContractsPage /></AdminRoute>} />
      <Route path="/calendar" element={<AdminRoute><CalendarPage /></AdminRoute>} />
      <Route path="/notifications" element={<AdminRoute><NotificationsPage /></AdminRoute>} />
      <Route path="/quotations" element={<AdminRoute><QuotationsPage /></AdminRoute>} />
      <Route path="/staff" element={<AdminRoute><StaffPage /></AdminRoute>} />
      <Route path="/services" element={<AdminRoute><ServicesPage /></AdminRoute>} />
      <Route path="/security" element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/cleaning" element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/water-treatment" element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/garden" element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/pool" element={<AdminRoute><ServiceDetailPage /></AdminRoute>} />
      <Route path="/payments" element={<AdminRoute><PaymentTrackerPage /></AdminRoute>} />
      <Route path="/visitors" element={<AdminRoute><VisitorsPage /></AdminRoute>} />
      <Route path="/members" element={<AdminRoute><ApartmentMembersPage /></AdminRoute>} />
      <Route path="/bookings" element={<AdminRoute><BookingsPage /></AdminRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/users" element={<SuperAdminRoute><UsersPage /></SuperAdminRoute>} />
      <Route path="/gas-readings" element={<ProtectedRoute><GasReadingsPage /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
      <Route path="*" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
